import { apiClient } from './client';
import type { Guest, CreateGuestDto, UpdateGuestDto } from '@/types/guest.types';
import type { Event } from '@/types/event.types';
import type { InvitationGroups, ClaimableInvitation } from '@/types/invitation.types';
import type { LandingThemeConfig } from '@/types/celebration.types';

export interface BulkGuestEventOperation {
  clientChangeId: string;
  action: 'assign' | 'unassign';
  event_id: number;
  guest_id?: number;
  user_id?: number;
}

export interface BulkGuestEventOperationResult {
  clientChangeId: string;
  status: 'success' | 'failed';
  action: 'assign' | 'unassign';
  eventId: number;
  guestId?: number;
  userId?: number;
  retryable?: boolean;
  code?: string;
  message?: string;
}

export interface BulkGuestEventOperationResponse {
  status: 'success' | 'partial' | 'failed';
  summary: {
    requested: number;
    succeeded: number;
    failed: number;
    retryableFailed: number;
    nonRetryableFailed: number;
  };
  results: BulkGuestEventOperationResult[];
}

export interface HostQuotaSummaryHost {
  hostUserId: number;
  hostName: string;
  hostNickname?: string;
  celebrationQuota: number | null;
  celebrationUsedSeats: number;
  celebrationRemainingSeats: number | null;
  celebrationStatus: 'ok' | 'warning' | 'unlimited';
}

export interface HostQuotaSummaryEvent {
  eventId: number;
  eventTitle: string;
  hostUserId: number;
  hostName: string;
  hostNickname?: string;
  allocatedSeats: number | null;
  usedSeats: number;
  remainingSeats: number | null;
  status: 'ok' | 'full' | 'over' | 'unlimited';
}

export interface HostQuotaSummaryResponse {
  hosts: HostQuotaSummaryHost[];
  eventQuotas: HostQuotaSummaryEvent[];
}

export interface InvitationReadinessCheck {
  key: 'slug' | 'events';
  label: string;
  satisfied: boolean;
  message: string;
}

export interface InvitationReadinessResponse {
  ready: boolean;
  celebrationId: number;
  guestLandingUrl?: string;
  checks: InvitationReadinessCheck[];
  message: string;
}

export interface BulkInvitationSendResult {
  guestId: number;
  guestName: string;
  status: 'success' | 'failed';
  message?: string;
}

export interface BulkInvitationSendResponse {
  status: 'success' | 'partial' | 'failed';
  summary: {
    requested: number;
    succeeded: number;
    failed: number;
  };
  results: BulkInvitationSendResult[];
}

export interface InvitationEmailPreviewResponse {
  subject: string;
  html: string;
  guestLandingUrl: string;
}

export interface TestInvitationEmailResponse {
  recipient: string;
  previewUrl?: string;
}

export interface CelebrationGuestDirectoryResponse {
  guests: Guest[];
  registrationToken?: string;
}

export interface EventHostQuotaUpsertInput {
  event_id: number;
  host_user_id: number;
  allocated_seats: number | null;
}

export class GuestService {
  async getGuestDetails(guestId: number): Promise<Guest> {
    const response = await apiClient.get(`/guests/${guestId}`);
    return response.data.data;
  }

  async sendInvitation(eventId: number, guestId: number): Promise<{ token: string }> {
    void eventId;
    const response = await apiClient.post(`/guests/${guestId}/send-invitation`);
    return response.data;
  }

  async getInvitationReadiness(celebrationId: number): Promise<InvitationReadinessResponse> {
    const response = await apiClient.get(`/guests/celebrations/${celebrationId}/invitation-readiness`);
    return response.data.data;
  }

  async sendBulkInvitations(celebrationId: number, guestIds: number[]): Promise<BulkInvitationSendResponse> {
    const response = await apiClient.post(`/guests/celebrations/${celebrationId}/send-invitations`, {
      guest_ids: guestIds,
    });
    return response.data.data;
  }

  async getInvitationEmailPreview(
    celebrationId: number,
    landingTheme?: LandingThemeConfig,
  ): Promise<InvitationEmailPreviewResponse> {
    const response = await apiClient.post(`/guests/celebrations/${celebrationId}/invitation-email-preview`, {
      landing_theme: landingTheme,
    });
    return response.data.data;
  }

  async sendTestInvitationEmail(
    celebrationId: number,
    recipientEmail: string,
    landingTheme?: LandingThemeConfig,
  ): Promise<TestInvitationEmailResponse> {
    const response = await apiClient.post(`/guests/celebrations/${celebrationId}/send-test-email`, {
      recipient_email: recipientEmail,
      landing_theme: landingTheme,
    });
    return response.data.data;
  }

  async getEventGuests(eventId: number, cacheBust = false): Promise<Guest[]> {
    const url = `/guests/events/${eventId}`;
    const response = await apiClient.get(url, {
      params: cacheBust ? { cacheBust: true } : undefined
    });
    return response.data.data;
  }

  async getEventDetails(eventId: number): Promise<Event> {
    const response = await apiClient.get(`/events/${eventId}`);
    return response.data as Event;
  }

  async createGuest(eventId: number, guestData: CreateGuestDto): Promise<Guest> {
    const response = await apiClient.post(`/guests/events/${eventId}`, guestData);
    return response.data.data;
  }

  async bulkAddGuests(eventId: number, guests: CreateGuestDto[]): Promise<Guest[]> {
    const response = await apiClient.post(`/guests/events/${eventId}/bulk`, { guests });
    return response.data.guests || [];
  }

  async updateGuest(eventId: number, guestId: number, guestData: UpdateGuestDto): Promise<Guest> {
    const response = await apiClient.put(`/guests/events/${eventId}/${guestId}`, guestData);
    return response.data.data;
  }

  async deleteGuest(eventId: number, guestId: number): Promise<void> {
    await apiClient.delete(`/guests/events/${eventId}/${guestId}`);
  }

  async updateRSVP(eventId: number, guestId: number, status: string): Promise<Guest> {
    const response = await apiClient.put(`/guests/events/${eventId}/${guestId}/rsvp`, { rsvp_status: status });
    return response.data.data;
  }

  async getGuestsByEvent(eventId: number): Promise<Guest[]> {
    const response = await apiClient.get(`/guests/event/${eventId}`);
    return response.data.data;
  }

  // Celebration-level guest methods
  async getCelebrationGuests(celebrationId: number, cacheBust = false): Promise<Guest[]> {
    const response = await apiClient.get(`/guests/celebrations/${celebrationId}`, {
      params: cacheBust ? { _t: Date.now() } : undefined
    });
    return response.data.data;
  }

  async getCelebrationGuestDirectory(celebrationId: number): Promise<CelebrationGuestDirectoryResponse> {
    const response = await apiClient.get(`/guests/celebrations/${celebrationId}`);
    return {
      guests: response.data.data,
      registrationToken: response.data.invite?.registration_token || undefined,
    };
  }

  async addCelebrationGuests(celebrationId: number, guests: CreateGuestDto[]): Promise<Guest[]> {
    const response = await apiClient.post(`/guests/celebrations/${celebrationId}`, { guests });
    return response.data.data || [];
  }

  async updateGuestEvent(guestId: number, eventId: number | null): Promise<Guest> {
    const response = await apiClient.patch(`/guests/${guestId}/event`, { event_id: eventId });
    return response.data.data;
  }

  async bulkUpdateGuestEvents(
    celebrationId: number,
    operations: BulkGuestEventOperation[]
  ): Promise<BulkGuestEventOperationResponse> {
    const response = await apiClient.patch(
      `/guests/celebrations/${celebrationId}/event-assignments/bulk`,
      { operations }
    );
    return response.data.data;
  }

  async getHostQuotaSummary(celebrationId: number): Promise<HostQuotaSummaryResponse> {
    const response = await apiClient.get(`/guests/celebrations/${celebrationId}/host-quotas`);
    return response.data.data;
  }

  async upsertEventHostQuotas(
    celebrationId: number,
    quotas: EventHostQuotaUpsertInput[]
  ): Promise<HostQuotaSummaryResponse> {
    const response = await apiClient.put(`/guests/celebrations/${celebrationId}/host-quotas`, {
      quotas,
    });
    return response.data.data;
  }

  async deleteCelebrationGuest(celebrationId: number, guestId: number): Promise<void> {
    // Use celebration ID as placeholder for eventId since celebration-level guests have no event
    await apiClient.delete(`/guests/events/${celebrationId}/${guestId}`);
  }

  async getMyInvitations(): Promise<InvitationGroups> {
    const response = await apiClient.get('/guests/invitations');
    return response.data.data;
  }

  async updateInvitationRSVP(guestId: number, data: {
    rsvp_status: string;
    notes?: string;
    meal_preference?: string;
    dietary_restrictions?: string;
    plus_one_name?: string;
  }): Promise<Guest> {
    const response = await apiClient.put(`/guests/invitations/${guestId}/rsvp`, data);
    return response.data.data;
  }

  async searchClaimableInvitations(email: string): Promise<ClaimableInvitation[]> {
    const response = await apiClient.post('/guests/invitations/claim/search', { email });
    return response.data.data || [];
  }

  async claimInvitations(email: string, guestIds: number[]): Promise<{ claimed_count: number }> {
    const response = await apiClient.post('/guests/invitations/claim', {
      email,
      guest_ids: guestIds,
    });

    return {
      claimed_count: response.data.claimed_count || 0,
    };
  }
}

export const guestService = new GuestService();
